import { useEffect, useMemo, useState } from "react";
import Loader from "examples/Loader";
import { useAppServices } from "hook/services";
import Domain from "../settings/Configurator/components/BasicInfo/BrandingTabs/Domain";
import Menu from "../settings/Configurator/components/BasicInfo/BrandingTabs/Menu";
import Leads from "../Leads";
import DefaultEmail from "../components/Ticket/components/DefaultEmail";
import useAgencyTheme from "./useAgencyTheme";
import resolvePanel from "./resolvePanel";
import ThemeStep from "./ThemeStep";
import PremiumStep from "./PremiumStep";

function firstIncompleteIndex(steps, completed) {
  const index = steps.findIndex((step) => step?.key && !completed.includes(step.key));
  return index === -1 ? 0 : index;
}

function OnboardingWizard({ steps, agency, Update }) {
  const Service = useAppServices();
  const completed = agency?.onboarding || [];
  const [index, setIndex] = useState(() => firstIncompleteIndex(steps, completed));
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [domainOpen, setDomainOpen] = useState(false);
  const [domainMessageDisconnect, setDomainMessageDisconnect] = useState("");
  const { selectedTheme, loading: themeLoading, buttonLoading, handelThemeData, reloadTheme } =
    useAgencyTheme(agency?._id);

  const step = steps[index];
  const panel = useMemo(() => resolvePanel(step), [step]);
  const total = steps.length;
  const progress = Math.round((completed.filter((key) => steps.some((stepItem) => stepItem.key === key)).length / total) * 100);

  useEffect(() => {
    setReady(false);
  }, [index, panel]);

  useEffect(() => {
    if (panel === "domain") {
      setReady(!themeLoading && Boolean(agency?.domain));
    }
    if (panel === "menu") {
      setReady(!themeLoading && Boolean(selectedTheme?.theme_id));
    }
    if (panel === "email" || panel === "confirm") {
      setReady(true);
    }
  }, [panel, agency?.domain, selectedTheme?.theme_id]);

  const markDone = async () => {
    if (!step?.key) return false;
    if (completed.includes(step.key)) return true;

    const payload = {
      _id: agency?._id,
      onboarding: [...completed, step.key],
    };
    const { response } = await Service.agency.update({ payload });
    if (!response?.success) return false;
    Update?.(response.data);
    return true;
  };

  const handleNext = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const ok = await markDone();
      if (!ok) return;
      const nextCompleted = [...completed, step.key];
      const nextIndex = steps.findIndex(
        (item, itemIndex) => itemIndex > index && item?.key && !nextCompleted.includes(item.key)
      );
      if (nextIndex !== -1) setIndex(nextIndex);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (index === 0) return;
    setIndex(index - 1);
  };

  return (
    <div className="sd-onboarding">
      <style>{`
        .sd-onboarding {
          position: fixed;
          inset: 0;
          z-index: 1000;
          background: #f8fafc;
          display: flex;
          flex-direction: column;
          font-family: Inter, system-ui, sans-serif;
          color: #0f172a;
        }
        .sd-onboarding__bar {
          height: 6px;
          background: #e2e8f0;
        }
        .sd-onboarding__bar > span {
          display: block;
          height: 100%;
          background: #00a6a9;
        }
        .sd-onboarding__header,
        .sd-onboarding__footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 20px 32px;
        }
        .sd-onboarding__header h1 {
          margin: 0 0 4px;
          font-size: 22px;
          font-weight: 650;
        }
        .sd-onboarding__header p,
        .sd-onboarding__note {
          margin: 0;
          color: #64748b;
          font-size: 14px;
        }
        .sd-onboarding__count {
          font-size: 14px;
          font-weight: 600;
          color: #00a6a9;
          white-space: nowrap;
        }
        .sd-onboarding__body {
          flex: 1;
          overflow: auto;
          padding: 8px 32px 24px;
        }
        .sd-onboarding__help {
          margin-top: 8px;
          background: transparent;
          border: 0;
          color: #00a6a9;
          font-weight: 600;
          cursor: pointer;
          padding: 0;
        }
        .sd-onboarding__footer {
          border-top: 1px solid #e2e8f0;
          background: #fff;
        }
        .sd-onboarding__actions {
          display: flex;
          gap: 10px;
          margin-left: auto;
        }
        .sd-onboarding__btn {
          border-radius: 10px;
          padding: 10px 18px;
          font-weight: 650;
          cursor: pointer;
          border: 1px solid #cbd5e1;
          background: #fff;
          color: #0f172a;
        }
        .sd-onboarding__btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .sd-onboarding__btn--next {
          background: #00a6a9;
          border-color: #00a6a9;
          color: #fff;
        }
        .sd-onboarding__stripe {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          align-items: center;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px 18px;
          margin-bottom: 16px;
        }
        .sd-onboarding__stripe h3 {
          margin: 0 0 4px;
          font-size: 16px;
        }
        .sd-onboarding__link-btn,
        .sd-onboarding__badge {
          border-radius: 10px;
          padding: 10px 16px;
          font-weight: 650;
          text-decoration: none;
          white-space: nowrap;
        }
        .sd-onboarding__link-btn {
          background: #00a6a9;
          color: #fff;
        }
        .sd-onboarding__badge {
          background: #dcfce7;
          color: #166534;
        }
        .sd-onboarding__theme .MuiGrid-container {
          align-items: stretch;
        }
      `}</style>

      <div className="sd-onboarding__bar">
        <span style={{ width: `${progress}%` }} />
      </div>

      <header className="sd-onboarding__header">
        <div>
          <h1>{step?.label || "Welcome to Smart Directory"}</h1>
          <p>{step?.value || "Finish this step to continue into the app."}</p>
          {step?.kb_link && (
            <button
              type="button"
              className="sd-onboarding__help"
              onClick={() => window.open(step.kb_link, "_blank", "noopener,noreferrer")}
            >
              View help article
            </button>
          )}
        </div>
        <div className="sd-onboarding__count">
          Step {index + 1} of {total}
        </div>
      </header>

      <div className="sd-onboarding__body">
        {themeLoading && (panel === "theme" || panel === "domain" || panel === "menu") ? (
          <Loader />
        ) : (
          <>
            {panel === "theme" && (
              <ThemeStep
                agency={agency}
                selectedTheme={selectedTheme}
                handelThemeData={handelThemeData}
                onReady={setReady}
              />
            )}
            {panel === "domain" && (
              <Domain
                key={selectedTheme?.theme_id || "domain"}
                selectedTheme={selectedTheme}
                buttonLoading={buttonLoading}
                open={domainOpen}
                setOpen={setDomainOpen}
                handleRefresh={reloadTheme}
                domainMessageDisconnect={domainMessageDisconnect}
                setDomainMessageDisconnect={setDomainMessageDisconnect}
              />
            )}
            {panel === "menu" && (
              <Menu handelThemeData={handelThemeData} selectedTheme={selectedTheme} />
            )}
            {panel === "premium" && <PremiumStep onReady={setReady} />}
            {panel === "leads" && <Leads onLaunched={() => setReady(true)} />}
            {panel === "email" && (
              <div>
                <p className="sd-onboarding__note">
                  {agency?.ghl?.location_id
                    ? "LeadConnector is connected for this location. Customize the email template, save it, then continue."
                    : "Customize the email template and save it. LeadConnector uses the GoHighLevel location on this account."}
                </p>
                <DefaultEmail />
              </div>
            )}
            {panel === "confirm" && (
              <p className="sd-onboarding__note">
                Complete this setup, then continue. You can change it later from the same page in the app.
              </p>
            )}
          </>
        )}
      </div>

      <footer className="sd-onboarding__footer">
        <span className="sd-onboarding__note">
          {ready ? "This step is ready." : "Finish this step to continue."}
        </span>
        <div className="sd-onboarding__actions">
          <button type="button" className="sd-onboarding__btn" onClick={handleBack} disabled={index === 0 || saving}>
            Back
          </button>
          <button
            type="button"
            className="sd-onboarding__btn sd-onboarding__btn--next"
            onClick={handleNext}
            disabled={!ready || saving}
          >
            {saving ? "Saving..." : index === total - 1 ? "Finish" : "Next"}
          </button>
        </div>
      </footer>
    </div>
  );
}

export default OnboardingWizard;
